import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useBehaviorSubject } from '@euler/utils/hooks/useBehaviorSubject';
import { ArrowUp, Mic } from 'lucide-react-native';
import { memo, useEffect, useRef } from 'react';
import { Animated, Pressable, View } from 'react-native';
import { aiColors, tint } from '../../../../../ai-suite/theme';
import { HoldToTalkProps } from './holdToTalkTypes';

/** A press held this long is a hold: letting go sends. */
const kHoldMs = 400;
/**
 * Pressed at once: react-native-web otherwise waits 50 ms, and a quicker
 * click is no press at all. Its own prop, which React Native's types lack.
 */
const kPressAtOnce = { delayPressIn: 0 };

/**
 * The mic on the web: tap to start talking and tap again to send, or hold
 * and let go as on a phone. Esc or 取消 drops what was said. A tap suits a
 * mouse, and the browser's microphone prompt, which a hold would have to
 * be let go for.
 */
export const HoldToTalk = memo(
  ({
    enabled,
    talking,
    level$,
    onPress,
    onRelease,
    onCancel,
  }: HoldToTalkProps) => {
    const { rpx } = useResponsiveContext();
    const [level] = useBehaviorSubject(level$);
    const scale = useRef(new Animated.Value(1)).current;
    useEffect(() => {
      Animated.timing(scale, {
        toValue: talking ? 1.15 + level * 0.45 : 1,
        duration: 100,
        useNativeDriver: true,
      }).start();
    }, [level, scale, talking]);
    // When the current press started talking; null when it did not.
    const pressedAt = useRef<number | null>(null);
    // Read at press time: a press outlives the render it started in.
    const latest = useRef({ talking, onPress, onRelease, onCancel });
    latest.current = { talking, onPress, onRelease, onCancel };
    useEffect(() => {
      if (!talking) return;
      const keydown = (event: KeyboardEvent) => {
        if (event.key !== 'Escape' || event.repeat) return;
        event.preventDefault();
        latest.current.onCancel();
      };
      document.addEventListener('keydown', keydown);
      return () => document.removeEventListener('keydown', keydown);
    }, [talking]);
    // Unmounted while talking: nothing will send it, so it is dropped.
    useEffect(
      () => () => {
        if (latest.current.talking) latest.current.onCancel();
      },
      [],
    );
    const size = rpx(44);
    return (
      <View style={{ width: size, height: size }}>
        {talking ? (
          // Wide enough for the hint: an absolute child laid out inside the
          // mic alone gets the mic's width and its text is clipped.
          <View
            pointerEvents="box-none"
            style={{
              position: 'absolute',
              right: 0,
              bottom: size + rpx(10),
              width: rpx(200),
              alignItems: 'flex-end',
            }}
          >
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                borderRadius: rpx(999),
                paddingHorizontal: rpx(10),
                paddingVertical: rpx(4),
                backgroundColor: aiColors.foreground,
              }}
            >
              <Typography.Text
                size={rpx(11)}
                color={aiColors.primaryForeground}
                numberOfLines={1}
              >
                {'点击发送 · '}
              </Typography.Text>
              <Pressable accessibilityRole="button" onPress={onCancel}>
                <Typography.Text
                  size={rpx(11)}
                  color={aiColors.primaryForeground}
                  numberOfLines={1}
                  style={{ textDecorationLine: 'underline' }}
                >
                  取消
                </Typography.Text>
              </Pressable>
            </View>
          </View>
        ) : null}
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={talking ? '发送' : '点击说话'}
          disabled={!enabled && !talking}
          {...kPressAtOnce}
          // A touch held long would end the press, and send, at the OS
          // long-press; handling it keeps the hold going.
          onLongPress={() => undefined}
          onPressIn={() => {
            if (latest.current.talking) return;
            pressedAt.current = Date.now();
            latest.current.onPress();
          }}
          onPressOut={() => {
            const at = pressedAt.current;
            pressedAt.current = null;
            // The tap that started talking leaves it on; a hold, or the
            // next tap, sends.
            if (at != null && Date.now() - at < kHoldMs) return;
            if (latest.current.talking) latest.current.onRelease();
          }}
          style={{
            width: size,
            height: size,
            alignItems: 'center',
            justifyContent: 'center',
            opacity: enabled || talking ? 1 : 0.4,
          }}
        >
          <Animated.View
            style={{
              position: 'absolute',
              width: size,
              height: size,
              borderRadius: size / 2,
              backgroundColor: tint(aiColors.primary, 0.25),
              opacity: talking ? 1 : 0,
              transform: [{ scale }],
            }}
          />
          <View
            style={{
              width: size,
              height: size,
              borderRadius: size / 2,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: aiColors.primary,
            }}
          >
            {talking ? (
              <ArrowUp size={rpx(20)} color={aiColors.primaryForeground} />
            ) : (
              <Mic size={rpx(20)} color={aiColors.primaryForeground} />
            )}
          </View>
        </Pressable>
      </View>
    );
  },
);
